import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Select from "react-select";
import Logo from "../assets/img/Logo.png";
import Sidebar from "../components/Sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faGlobe } from "@fortawesome/free-solid-svg-icons";
import "../assets/css/header.css";
import {
  getProfiles,
  getCurrentUser,
  getSugerencias,
} from "../services/API";
import { useNavigate } from "react-router-dom";
import alertify from "alertifyjs";
import "alertifyjs/build/css/alertify.css";
import "alertifyjs/build/css/themes/default.css";

function Header() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const navigate = useNavigate();
  const [selectedAccountLabel, setSelectedAccountLabel] =
    useState("JandSProducts");
  const [selectedAccountCountry, setSelectedAccountCountry] = useState("US");
  const [user, setUser] = useState("Name not available");
  const [sugerencias, setSugerencias] = useState([]);

  const formatOptionLabel = ({ label, value }) => (
    <div style={{ lineHeight: "1.2" }}>
      <span style={{ display: "block", marginBottom: "5px" }}>
        {label.split(' (')[0]}
        <span style={{
          background: '#007bff',
          color: '#fff',
          borderRadius: '6px',
          padding: '2px 8px',
          fontSize: '0.75em',
          marginLeft: '8px',
          display: 'inline-block',
        }}>
          {label.match(/\(([^)]+)\)/)?.[1]}
        </span>
      </span>
      <span style={{ fontSize: "0.8em", color: "#000", display: "block" }}>
        ID: {value}
      </span>
      <span style={{ fontSize: "0.75em", color: "#555", display: "block" }}>
        Country: {accounts.find((acc) => acc.value === value)?.country || "US"}
      </span>
    </div>
  );

  useEffect(() => {
    const storedLabel = localStorage.getItem("selectedAccountLabel");
    const storedCountry = localStorage.getItem("selectedAccountCountry");
    if (storedLabel) setSelectedAccountLabel(storedLabel);
    else setSelectedAccountLabel("No account selected");
    if (storedCountry) setSelectedAccountCountry(storedCountry);
  }, []);

  const dropdown1Ref = useRef(null);
  const dropdown3Ref = useRef(null);
  const dropdownRefs = useMemo(() => ({
    dropdown1: dropdown1Ref,
    dropdown3: dropdown3Ref,
  }), [dropdown1Ref, dropdown3Ref]);

  const toggleDropdown = useCallback((dropdownName) => {
    setActiveDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [profilesResp, currentUser, fetchedSugerencias] = await Promise.all([
          getProfiles().catch((err) => {
            console.warn("Failed to load Amazon profiles:", err.message);
            return { data: [] };
          }),
          getCurrentUser(),
          getSugerencias(),
        ]);

        if (currentUser.userRolesId === 1 || currentUser.userRolesId === 2) {
          const currentSelectedAccountLabel = localStorage.getItem(
            "selectedAccountLabel",
          );
          if (
            fetchedSugerencias &&
            Array.isArray(fetchedSugerencias.data) &&
            currentSelectedAccountLabel
          ) {
            const filteredSugerencias = fetchedSugerencias.data.filter(
              (sug) => sug.ads_account === currentSelectedAccountLabel,
            );
            setSugerencias(filteredSugerencias);
          }
        }

        setUser(currentUser.firstName + " " + currentUser.lastName);
        localStorage.setItem(
          "name",
          currentUser.firstName + " " + currentUser.lastName,
        );

        const profiles = Array.isArray(profilesResp.data) ? profilesResp.data : [];

        if (profiles.length === 0) {
          console.warn(
            "No Amazon profiles available. Amazon authentication may be required."
          );
          setAccounts([]);
          return;
        }

        // Mapping profiles to options
        const finalResult = profiles.map((p) => ({
          label: `${p.accountInfo?.name} (${p.countryCode})` || `Profile ${p.profileId} (${p.countryCode})`,
          value: String(p.profileId),
          country: p.countryCode || "US",
        }));

        if (
          currentUser.userRolesId === 2 ||
          currentUser.userRolesId === 3 ||
          currentUser.userRolesId === 4
        ) {
          const profiles = JSON.parse(currentUser.profilesId);
          const filteredAccounts =
            finalResult.filter((account) => profiles.includes(account.value));
          setAccounts(filteredAccounts);
        } else setAccounts(finalResult);
      } catch (error) {
        console.error("Error loading header data:", error);
        // Set default user name if everything fails
        setUser("User");
        setAccounts([]);
      }
    })();
  }, []);

  useEffect(() => {
    const selectedValue = localStorage.getItem("selectedAccountValue");
    if (!selectedValue || accounts.length === 0) return;

    const matched = accounts.find((acc) => acc.value === selectedValue);
    if (matched?.country) {
      setSelectedAccountCountry(matched.country);
      localStorage.setItem("selectedAccountCountry", matched.country);
    }
  }, [accounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.values(dropdownRefs).some(
          (ref) => ref.current && ref.current.contains(event.target),
        )
      )
        setActiveDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRefs]);

  const handleAccountChange = (selected) => {
    if (selected) {
      alertify
        .confirm(
          "Confirm Account Switch",
          "Are you sure you want to switch to account " +
            selected.label +
            "",
          () => {
            localStorage.setItem("selectedAccountLabel", selected.label);
            localStorage.setItem("selectedAccountValue", selected.value);
            localStorage.setItem("selectedAccountName", selected.label);
            localStorage.setItem("selectedAccountCountry", selected.country || "US");
            setSelectedAccountLabel(selected.label);
            setSelectedAccountCountry(selected.country || "US");
            setActiveDropdown(null);

            // Disparar evento personalizado para notificar el cambio
            window.dispatchEvent(new Event("accountChanged"));

            alertify.success("Account switched to " + selected.label);
            alertify.warning("Refreshing page...");

            setTimeout(() => {
              window.location.reload();
            }, 2000);
          },
          () => {
            setActiveDropdown(null);
            alertify.error("Account switch canceled");
          },
        ).set("labels", { ok: "Yes, switch", cancel: "Cancel" });
    } else {
      setActiveDropdown(null);
    }
  };

  const handleViewAllSugerencias = () => {
    navigate("/sugerencias");
    setActiveDropdown(null);
  };

  return (
    <nav className="navbar navbar-expand-lg bg-body-tertiary border-bottom border-2 border-dark fixed-top">
      <div className="container-fluid">
        <Sidebar />
        <a className="navbar-brand d-flex align-items-center" href="/dashboard">
          <img src={Logo} alt="Logo" width={40} className="me-2" /> Jungle Click
        </a>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto">
            <li className="nav-item">
              <button
                className="btn btn-link nav-link"
                onClick={() => navigate("/dashboard")}
              >
                Home
              </button>
            </li>

            <li
              className="nav-item dropdown justify-content-start mx-2"
              ref={dropdownRefs.dropdown3}
            >
              <button
                className="btn btn-link nav-link dropdown-toggle w-auto"
                onClick={() => toggleDropdown("dropdown3")}
                aria-expanded={activeDropdown === "dropdown3"}
              >
                <FontAwesomeIcon icon={faGlobe} className="me-2" />{" "}
                {selectedAccountLabel}
                <span className="badge bg-light text-dark border ms-2">
                  {selectedAccountCountry || "US"}
                </span>
              </button>
              {activeDropdown === "dropdown3" && (
                <div
                  className="dropdown-menu show p-2"
                  style={{
                    minWidth: "250px",
                    maxWidth: "350px",
                  }}
                >
                  <div className="mb-2 border-bottom pb-2">
                    <span className="fw-bold">Current Account:</span>{" "}
                    {selectedAccountLabel}
                    <span className="badge bg-light text-dark border ms-2">
                      {selectedAccountCountry || "US"}
                    </span>
                  </div>
                  <Select
                    options={accounts}
                    placeholder="Search account..."
                    isClearable
                    isSearchable={true}
                    onChange={handleAccountChange}
                    value={null}
                    formatOptionLabel={formatOptionLabel}
                    styles={{
                      container: (base) => ({ ...base, width: "100%" }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        position: "relative",
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }),
                      option: (base, state) => ({
                        ...base,
                        padding: "8px 12px",
                      }),
                    }}
                  />
                </div>
              )}
            </li>

            <li
              className="nav-item dropdown justify-content-start mx-2"
              ref={dropdownRefs.dropdown1}
            >
              <button
                className="btn btn-link nav-link"
                onClick={() => toggleDropdown("dropdown1")}
                aria-expanded={activeDropdown === "dropdown1"}
              >
                <FontAwesomeIcon icon={faBell} />
                {sugerencias.length > 0 && (
                  <span className="badge bg-primary ms-1">
                    {sugerencias.length}
                  </span>
                )}
              </button>
              {activeDropdown === "dropdown1" && (
                <ul className="dropdown-menu show">
                  {sugerencias.length > 0 ? (
                    <>
                      {sugerencias.slice(0, 5).map((sug, index) => (
                        <li key={index}>
                          <span className="dropdown-item w-auto">
                            User recommendation {sug.usuario}
                          </span>
                        </li>
                      ))}
                      {sugerencias.length > 0 && (
                        <li>
                          <button
                            className="dropdown-item text-center fw-bold"
                            onClick={handleViewAllSugerencias}
                          >
                            View all
                          </button>
                        </li>
                      )}
                    </>
                  ) : (
                    <li>
                      <span className="dropdown-item w-auto">
                        No notifications.
                      </span>
                    </li>
                  )}
                </ul>
              )}
            </li>

            <li className="nav-item mx-2">
              <span className="nav-link text-start">{user}</span>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default Header;
